import moment from 'moment';
import type { RoomExtra, RoomExtras } from '@features/rooms';
import type {
  ExtrasEditOption,
  ExtrasEditResult,
  ExtrasEditSelection,
  ExtrasGuest,
  ExtrasItem,
} from 'voyager';
import { RoomRateFormValues } from '../../RoomRateForm.schema';

export type ExtraFormEntry = {
  isSelected?: boolean;
  quantity?: number;
  option?: number;
  optionSelections?: ExtraOptionSelectionState;
  guestSelections?: string[];
};

export type ExtraOptionSelectionState = {
  selections: ExtrasEditSelection[];
  applyToAll: boolean;
};

const DAY_KEY_FORMAT = 'YYYY-MM-DD';

const toDayKey = (value: string) => moment(value).format(DAY_KEY_FORMAT);

const formatTime = (value: string) => value.slice(0, 5);

const formatFullDay = (value: string, language: string) =>
  moment(value).locale(language).format('dddd, D MMMM');

const formatRangeDay = (value: string, language: string, withMonth: boolean) =>
  moment(value)
    .locale(language)
    .format(withMonth ? 'ddd, D MMMM' : 'ddd, D');

export const getExtraImageUrl = (extra: RoomExtra) => {
  const { image } = extra;
  if (Array.isArray(image)) {
    return image[0]?.url;
  }
  return image?.url ?? undefined;
};

export const buildExtraOptions = (
  extra: RoomExtra,
  language: string
): ExtrasEditOption[] | undefined => {
  const price = extra.price ?? undefined;
  switch (extra.type) {
    case 'per_date':
      return extra.options.map((option, index) => ({
        id: String(index),
        label: formatFullDay(option.startDate, language),
        price,
      }));
    case 'per_date_range':
      return extra.options.map((option, index) => {
        const sameMonth = moment(option.startDate).isSame(option.endDate, 'month');
        return {
          id: String(index),
          label: `${formatRangeDay(option.startDate, language, !sameMonth)} — ${formatRangeDay(
            option.endDate,
            language,
            true
          )}`,
          price,
        };
      });
    case 'per_time_range':
      return extra.options.map((option, index) => ({
        id: String(index),
        label: `${formatTime(option.startTime)} — ${formatTime(option.endTime)}`,
        price,
      }));
    case 'per_date_time':
      return extra.options.map((option, index) => ({
        id: String(index),
        label: formatTime(option.startTime),
        date: toDayKey(option.startDate),
        price,
      }));
    case 'per_date_time_range':
      return extra.options.map((option, index) => ({
        id: String(index),
        label: `${formatTime(option.startTime)} — ${formatTime(option.endTime)}`,
        date: toDayKey(option.startDate),
        price,
      }));
    default:
      return undefined;
  }
};

export const buildExtraRangeLabel = (extra: RoomExtra, language: string) => {
  if (extra.type !== 'per_date_time' && extra.type !== 'per_date_time_range') {
    return undefined;
  }
  const days = extra.options.map((option) => toDayKey(option.startDate)).sort();
  if (!days.length) {
    return undefined;
  }
  const format = (day: string) => moment(day).locale(language).format('D MMM');
  const first = days[0];
  const last = days[days.length - 1];
  return first === last ? format(first) : `${format(first)} — ${format(last)}`;
};

type BuildExtrasItemsParams = {
  extras: RoomExtras;
  entries: (ExtraFormEntry | undefined)[];
  optionSelections: Record<number, ExtraOptionSelectionState | undefined>;
  guestSelections: Record<number, string[] | undefined>;
  language: string;
  fallbackCurrencyCode: string;
};

export const buildExtrasItems = ({
  extras,
  entries,
  optionSelections,
  guestSelections,
  language,
  fallbackCurrencyCode,
}: BuildExtrasItemsParams): ExtrasItem[] =>
  extras.map((extra, index) => {
    const entry = entries[index];
    const optionState = optionSelections[index];
    const isSelected = !!entry?.isSelected;
    return {
      id: String(index),
      image: { src: getExtraImageUrl(extra), alt: extra.name },
      title: extra.name,
      price: extra.price ?? { amount: 0, currencyCode: fallbackCurrencyCode },
      selected: isSelected,
      quantity: isSelected ? entry?.quantity ?? 1 : undefined,
      selectedGuestIds: isSelected ? guestSelections[index] : undefined,
      type: extra.type,
      perPerson: !!extra.pricingType?.startsWith('per_person'),
      maxQuantity: extra.maxQuantity,
      options: buildExtraOptions(extra, language),
      selectedOptions: isSelected ? optionState?.selections : undefined,
      appliedToAllGuests: isSelected ? optionState?.applyToAll : undefined,
      rangeLabel: buildExtraRangeLabel(extra, language),
    };
  });

export const buildExtrasGuests = (
  guests: RoomRateFormValues['guests'] | undefined,
  fallbackLabel: (guestNumber: number) => string
): ExtrasGuest[] =>
  (guests ?? []).map((guest, index) => {
    const label = [guest.firstName, guest.lastName].filter(Boolean).join(' ').trim();
    return { id: String(index), label: label || fallbackLabel(index + 1) };
  });

export const buildExtrasBasketPayload = (
  entityExtras: RoomExtras | null,
  formExtras: (ExtraFormEntry | undefined)[] | undefined,
  optionSelections: Record<number, ExtraOptionSelectionState | undefined>
) =>
  (formExtras ?? []).reduce<Record<string, unknown>[]>((acc, cur, index) => {
    const entityExtra = entityExtras?.[index];
    if (!cur?.isSelected || !entityExtra) {
      return acc;
    }
    const {
      isSelected: _isSelected,
      option,
      quantity,
      optionSelections: _optionSelections,
      guestSelections: _guestSelections,
      ...formData
    } = cur;
    const { options, ...entityExtraWithNoOptions } = entityExtra as RoomExtra & {
      options?: unknown[];
    };

    const optionState = optionSelections[index];
    const selections = optionState?.selections ?? [];
    const distinctOptionIds = [...new Set(selections.map((selection) => selection.optionId))];

    if (options?.length && distinctOptionIds.length > 1) {
      const divergentEntries = distinctOptionIds.map((optionId) => ({
        ...entityExtraWithNoOptions,
        ...formData,
        quantity: selections.filter((selection) => selection.optionId === optionId).length,
        options: [options[Number(optionId)]],
      }));
      return [...acc, ...divergentEntries];
    }

    return [
      ...acc,
      {
        ...entityExtraWithNoOptions,
        ...formData,
        quantity: quantity || 1,
        ...(typeof option === 'number' && options?.length ? { options: [options[option]] } : {}),
      },
    ];
  }, []);

export const guestSelectionPatch = (selectedGuestIds: string[]): Partial<ExtraFormEntry> =>
  selectedGuestIds.length
    ? { isSelected: true, quantity: selectedGuestIds.length, guestSelections: selectedGuestIds }
    : { isSelected: false, guestSelections: selectedGuestIds };

export const editResultPatch = (
  extra: Pick<RoomExtra, 'pricingType'> | undefined,
  result: ExtrasEditResult,
  guestCount: number
): Partial<ExtraFormEntry> => {
  if (result.type === 'quantity') {
    return { isSelected: true, quantity: result.quantity };
  }
  const firstSelection = result.selections[0];
  if (!firstSelection) {
    return { isSelected: false };
  }
  const isPerPerson = !!extra?.pricingType?.startsWith('per_person');
  const quantity =
    result.applyToAll && isPerPerson && guestCount > 1 ? guestCount : result.selections.length;
  return {
    isSelected: true,
    option: Number(firstSelection.optionId),
    quantity,
    optionSelections: { selections: result.selections, applyToAll: result.applyToAll },
  };
};
